import { Command } from "commander";
import chalk from "chalk";
import ora from "ora";
import path from "path";
import fs from "fs";
import os from "os";
import { spawn } from "child_process";
import { checkJava } from "../checks/java.js";
import { checkAndroid } from "../checks/android.js";
import { checkIos } from "../checks/ios.js";
import { findPackageJson, directoryExists } from "../utils/files.js";
import { runCommand, runCommandStreaming } from "../utils/exec.js";
import { promptSelect, promptQuestion } from "../utils/prompt.js";
import { detectProjectType } from "../project/projectDetector.js";
import { resolvePackageManager } from "../packageManager/packageManagerService.js";
import { findIosIdentities, findProvisioningProfiles } from "../utils/iosSigning.js";
import { loadConfig, saveConfig } from "../config/configLoader.js";
import { checkAndResolvePortConflict } from "../utils/portConflictResolver.js";
import { setupAdbReverse } from "../utils/adbReverse.js";

export const runCommandObj = new Command("run")
  .description("Build, install and run the React Native application")
  .argument("[platform]", "Target platform ('android' or 'ios')")
  .option("--mode <mode>", "Build mode ('debug' or 'release')")
  .option("--device <device>", "Specific device serial number or simulator name/UDID to deploy to")
  .option("--scheme <scheme>", "iOS target scheme")
  .action(async (platformArg: string | undefined, options: { mode?: string; device?: string; scheme?: string }) => {
    const cwd = process.cwd();
    
    // Resolve platform dynamically
    let platform = platformArg ? platformArg.toLowerCase() : "";
    if (!platform) {
      if (os.platform() === "darwin") {
        if (process.stdout.isTTY) {
          platform = await promptSelect("Select target platform:", ["android", "ios"]);
        } else {
          platform = "android";
        }
      } else {
        platform = "android";
      }
    }

    if (platform !== "android" && platform !== "ios") {
      console.log(chalk.red(`✖ Platform '${platform}' is invalid. Supported platforms are 'android' and 'ios'.`));
      process.exit(1);
    }

    console.log(chalk.blue.bold(`🚀 Starting ${platform.toUpperCase()} Runner`));

    const packageJsonPath = findPackageJson(cwd);
    if (!packageJsonPath) {
      console.log(chalk.red("✖ Could not find package.json in the current directory or parent directories."));
      process.exit(1);
    }
    const projectRoot = path.dirname(packageJsonPath);

    // 1. Resolve build mode
    let mode = options.mode;
    if (!mode) {
      if (process.stdout.isTTY) {
        mode = await promptSelect("Select run mode:", ["debug", "release"]);
      } else {
        mode = "debug";
      }
    }

    // Identify project type
    const projectType = detectProjectType(projectRoot);
    const isExpo = projectType.startsWith("expo");

    // Verify expo-dev-client package for Expo projects
    if (isExpo) {
      const pkg = safeReadJson(packageJsonPath);
      const dependencies = pkg ? (pkg.dependencies as Record<string, unknown> || {}) : {};
      const devDependencies = pkg ? (pkg.devDependencies as Record<string, unknown> || {}) : {};
      const hasDevClient = "expo-dev-client" in dependencies || "expo-dev-client" in devDependencies;

      if (!hasDevClient) {
        console.log(chalk.blue("\nℹ 'expo-dev-client' is missing from project dependencies. Installing automatically..."));
        const pmResult = await resolvePackageManager(projectRoot);
        const pm = pmResult.selected;
        let installCmd = "";
        if (pm === "yarn") {
          installCmd = "yarn add expo-dev-client";
        } else if (pm === "pnpm") {
          installCmd = "pnpm add expo-dev-client";
        } else if (pm === "bun") {
          installCmd = "bun add expo-dev-client";
        } else {
          installCmd = "npm install expo-dev-client";
        }

        const installSpinner = ora(`Installing expo-dev-client using ${pm}...`).start();
        const installResult = await runCommand(installCmd);
        if (installResult.error) {
          installSpinner.fail("Failed to automatically install expo-dev-client. Please install it manually.");
          console.error(chalk.red(installResult.stderr || installResult.stdout));
        } else {
          installSpinner.succeed("Successfully installed expo-dev-client!");
        }
      }
    }

    if (mode === "debug") {
      await checkAndResolvePortConflict(8081);
    }

    if (platform === "android") {
      // --- ANDROID RUN PATHWAY ---
      const androidDir = path.join(projectRoot, "android");
      if (!directoryExists(androidDir)) {
        console.log(chalk.red(`✖ Android native directory not found. Run 'kyro prebuild android' first.`));
        process.exit(1);
      }

      // Verify environment
      const envSpinner = ora("Verifying Android environment...").start();
      const javaChecks = await checkJava();
      const androidChecks = await checkAndroid();
      if (javaChecks.some(c => c.status === "error") || androidChecks.some(c => c.status === "error")) {
        envSpinner.fail("Environment check failed!");
        process.exit(1);
      }
      envSpinner.succeed("Android environment verified.");

      // Check if pre-compiled APK exists
      let artifactPath = "";
      const artifactDir = path.join(androidDir, "app", "build", "outputs", "apk", mode);
      if (fs.existsSync(artifactDir)) {
        const files = fs.readdirSync(artifactDir);
        const apkFile = files.find(f => f.endsWith(".apk"));
        if (apkFile) {
          artifactPath = path.join(artifactDir, apkFile);
        }
      }

      let shouldRebuild = true;
      if (artifactPath) {
        console.log(chalk.green(`\nFound existing compiled APK: ${artifactPath}`));
        if (process.stdout.isTTY) {
          const choice = await promptSelect("What would you like to do?", [
            "Install and run existing build (Fast)",
            "Rebuild and run"
          ]);
          shouldRebuild = choice.includes("Rebuild");
        } else {
          shouldRebuild = false;
        }
      }

      if (shouldRebuild) {
        // Run full build
        const gradlewName = os.platform() === "win32" ? "gradlew.bat" : "./gradlew";
        const gradlewPath = path.join(androidDir, os.platform() === "win32" ? "gradlew.bat" : "gradlew");
        if (os.platform() !== "win32") {
          await runCommand(`chmod +x "${gradlewPath}"`);
        }

        const task = mode === "debug" ? "assembleDebug" : "assembleRelease";
        console.log(chalk.blue(`\n🔨 Compiling app: Running gradlew ${task}...`));
        const buildResult = await runCommandStreaming(`cd "${androidDir}" && ${gradlewName} ${task}`, {
          prefix: "gradle",
          verbose: true,
        });

        if (buildResult.code !== 0) {
          console.log(chalk.red(`✖ Build failed.`));
          process.exit(1);
        }

        // Re-locate built APK
        if (fs.existsSync(artifactDir)) {
          const files = fs.readdirSync(artifactDir);
          const apkFile = files.find(f => f.endsWith(".apk"));
          if (apkFile) {
            artifactPath = path.join(artifactDir, apkFile);
          }
        }
      }

      if (!artifactPath) {
        console.log(chalk.red("✖ Could not locate the built APK."));
        process.exit(1);
      }

      // Check devices
      const adbDevices = await runCommand("adb devices");
      const lines = adbDevices.stdout.split("\n").map(l => l.trim()).filter(l => l.length > 0);
      const devices = lines.slice(1).filter(l => l.endsWith("device")).map(l => l.split("\t")[0]);

      if (devices.length === 0) {
        console.log(chalk.yellow("\n⚠️ No connected devices found. Please plug in a device or launch an emulator."));
        process.exit(1);
      }

      let targetDevice = options.device;
      if (!targetDevice) {
        const config = loadConfig(projectRoot);
        const savedDefault = config.defaultAndroidDevice;
        if (savedDefault && devices.includes(savedDefault)) {
          console.log(chalk.green(`Using default Android device: ${savedDefault}`));
          targetDevice = savedDefault;
        } else {
          if (devices.length > 1) {
            if (process.stdout.isTTY) {
              targetDevice = await promptSelect("Multiple Android devices connected. Select target device:", devices);
              const saveAsDefault = await promptQuestion("Would you like to set this device as your default Android device? (y/n): ");
              if (saveAsDefault.toLowerCase() === "y" || saveAsDefault.toLowerCase() === "yes") {
                saveConfig(projectRoot, { defaultAndroidDevice: targetDevice });
                console.log(chalk.green(`Saved ${targetDevice} as your default Android device.`));
              }
            } else {
              targetDevice = devices[0];
            }
          } else {
            targetDevice = devices[0];
          }
        }
      }

      // Retrieve Package Name first
      let packageName = "";
      const buildGradlePath = path.join(androidDir, "app", "build.gradle");
      if (fs.existsSync(buildGradlePath)) {
        const buildGradleContent = fs.readFileSync(buildGradlePath, "utf-8");
        const match = buildGradleContent.match(/applicationId\s+"([^"]+)"/);
        if (match && match[1]) {
          packageName = match[1];
        }
      }
      if (!packageName) {
        const pkg = safeReadJson(packageJsonPath);
        if (pkg && pkg.name) {
          packageName = `com.${String(pkg.name).toLowerCase().replace(/[^a-z0-9]/g, "")}`;
        }
      }

      console.log(chalk.blue(`\n📲 Deploying APK to ${targetDevice}...`));
      const installSpinner = ora("Installing APK...").start();
      const installResult = await runCommand(`adb -s ${targetDevice} install -r "${artifactPath}"`);
      let installFailed = !!installResult.error;
      let installOutput = (installResult.stderr + "\n" + installResult.stdout).trim();

      if (installFailed && installOutput.includes("INSTALL_FAILED_UPDATE_INCOMPATIBLE")) {
        installSpinner.fail("Installation failed due to signature mismatch (previously installed build has conflicting signature).");
        if (process.stdout.isTTY) {
          const answer = await promptQuestion("Would you like to uninstall the conflicting package from the device and try again? (y/n): ");
          if (answer.toLowerCase() === "y" || answer.toLowerCase() === "yes") {
            const uninstallSpinner = ora(`Uninstalling package ${packageName || 'app'}...`).start();
            let uninstallRes = await runCommand(`adb -s ${targetDevice} uninstall "${packageName}"`);
            if (uninstallRes.error) {
              // Try fallback to handle multiple user profiles / guest profiles / secure folders
              uninstallRes = await runCommand(`adb -s ${targetDevice} shell pm uninstall --user all "${packageName}"`);
            }

            if (uninstallRes.error) {
              uninstallSpinner.fail("Failed to uninstall previous build.");
              console.error(chalk.red(uninstallRes.stderr || uninstallRes.stdout));
              console.log(chalk.yellow("\n💡 Troubleshooting Tips:"));
              console.log("1. Go to Settings -> Apps -> " + (packageName || "your app") + " on your device and manually uninstall it for all users.");
              console.log("2. Check if the app is active in Settings -> Security -> Device Administrators and disable it.");
              console.log("3. If using Samsung, check if the app is installed inside Secure Folder / Private Share and remove it.");
            } else {
              uninstallSpinner.succeed("Conflicting version uninstalled successfully.");
              const reinstallSpinner = ora("Re-installing built APK...").start();
              const reinstallResult = await runCommand(`adb -s ${targetDevice} install -r "${artifactPath}"`);
              if (reinstallResult.error) {
                reinstallSpinner.fail("Re-installation failed.");
                console.error(chalk.red(reinstallResult.stderr || reinstallResult.stdout));
              } else {
                reinstallSpinner.succeed("Installation successful!");
                installFailed = false;
              }
            }
          }
        }
      } else if (installFailed) {
        installSpinner.fail("Installation failed.");
        console.error(chalk.red(installOutput));
        process.exit(1);
      } else {
        installSpinner.succeed("APK Installed!");
      }

      if (!installFailed && packageName) {
        console.log(chalk.blue(`\n🚀 Opening: ${packageName}...`));
        await runCommand(`adb -s ${targetDevice} shell monkey -p ${packageName} -c android.intent.category.LAUNCHER 1`);

        if (mode === "debug") {
          console.log(chalk.blue("\n⚙️ Setting up reverse port mapping (8081)..."));
          await setupAdbReverse(targetDevice, 8081);
          
          console.log(chalk.blue("\n🌐 Launching Metro Server..."));
          const startCmd = isExpo ? "npx expo start --android" : "npm run start || npx react-native start";
          const metroProcess = spawn(os.platform() === "win32" ? "cmd.exe" : "sh", [
            os.platform() === "win32" ? "/c" : "-c",
            startCmd
          ], {
            cwd: projectRoot,
            stdio: "inherit"
          });
          
          await new Promise<void>((resolve) => {
            metroProcess.on("close", () => resolve());
            metroProcess.on("exit", () => resolve());
          });
        }
      }
    } else {
      // --- IOS RUN PATHWAY ---
      const iosDir = path.join(projectRoot, "ios");
      if (!directoryExists(iosDir)) {
        console.log(chalk.red(`✖ iOS native directory not found.`));
        process.exit(1);
      }

      // Verify environment
      const envSpinner = ora("Verifying iOS environment...").start();
      const iosChecks = await checkIos();
      if (iosChecks.some(c => c.status === "error")) {
        envSpinner.fail("iOS environment verification failed!");
        process.exit(1);
      }
      envSpinner.succeed("iOS environment verified.");

      // Find workspace or project
      const files = fs.readdirSync(iosDir);
      const workspaceFile = files.find(f => f.endsWith(".xcworkspace"));
      const projectFile = files.find(f => f.endsWith(".xcodeproj"));

      if (!workspaceFile && !projectFile) {
        console.log(chalk.red("✖ No iOS Xcode workspace or project found."));
        process.exit(1);
      }

      const buildTargetArg = workspaceFile 
        ? `-workspace "${path.join(iosDir, workspaceFile)}"` 
        : `-project "${path.join(iosDir, projectFile!)}"`;

      // Scan schemes using the .xcodeproj file to get only main app schemes (excluding pod targets)
      const listSpinner = ora("Scanning Xcode schemes...").start();
      const listCmd = `xcodebuild -list -project "${path.join(iosDir, projectFile!)}"`;
      const listResult = await runCommand(listCmd);
      listSpinner.succeed("Schemes scanned.");

      let schemes: string[] = [];
      const schemeMatches = listResult.stdout.match(/Schemes:\s*([\s\S]*)/i);
      if (schemeMatches && schemeMatches[1]) {
        schemes = schemeMatches[1]
          .split("\n")
          .map(s => s.trim())
          .filter(s => s.length > 0 && !s.startsWith("Information about"));
      }

      let scheme = options.scheme;
      if (!scheme && schemes.length > 0) {
        if (process.stdout.isTTY) {
          scheme = await promptSelect("Select Xcode scheme:", schemes);
        } else {
          scheme = schemes[0];
        }
      }

      if (!scheme) {
        console.log(chalk.red("✖ No target scheme selected or found."));
        process.exit(1);
      }

      // Check Development Team signing config in project.pbxproj
      const pbxprojPath = path.join(iosDir, projectFile!, "project.pbxproj");
      if (fs.existsSync(pbxprojPath)) {
        let pbxprojContent = fs.readFileSync(pbxprojPath, "utf-8");
        const hasTeamConfig = pbxprojContent.includes("DEVELOPMENT_TEAM");

        if (!hasTeamConfig) {
          console.log(chalk.yellow("\n⚠️  No Development Team ID found in Xcode project settings."));
          console.log("A development team is required for compiling/signing iOS applications.");
          
          // Resolve Bundle ID
          let bundleId = "";
          const pbxMatch = pbxprojContent.match(/PRODUCT_BUNDLE_IDENTIFIER\s*=\s*"?([^";\s]+)"?;/);
          if (pbxMatch && pbxMatch[1]) {
            bundleId = pbxMatch[1];
          }

          // Scan Keychain
          const identities = await findIosIdentities();
          const targetType = mode === "release" ? "distribution" : "development";
          let matchedIdentities = identities.filter(id => id.type === targetType);
          if (matchedIdentities.length === 0) {
            matchedIdentities = identities; // fallback
          }

          let selectedIdentity: typeof identities[0] | null = null;
          if (matchedIdentities.length > 0 && process.stdout.isTTY) {
            const optionsList = matchedIdentities.map(id => `${id.name} [${id.hash.slice(0, 8)}]`);
            const chosenStr = await promptSelect(`Select iOS Code Signing Certificate (${targetType}):`, [...optionsList, "Enter Team ID manually"]);
            if (chosenStr !== "Enter Team ID manually") {
              const idx = optionsList.indexOf(chosenStr);
              selectedIdentity = matchedIdentities[idx]!;
            }
          }

          let selectedTeam = "";
          let selectedProfileUuid = "";

          if (selectedIdentity) {
            selectedTeam = selectedIdentity.teamId;
            console.log(chalk.green(`Using signing identity: ${selectedIdentity.name}`));

            // Find matching provisioning profiles
            const allProfiles = findProvisioningProfiles();
            const matchedProfiles = allProfiles.filter(p => p.teamId === selectedTeam && (p.bundleId === bundleId || bundleId.startsWith(p.bundleId.replace("*", ""))));
            
            if (matchedProfiles.length > 0 && process.stdout.isTTY) {
              const profileOptions = matchedProfiles.map(p => `${p.name} (${p.uuid.slice(0, 8)})`);
              const chosenProfile = await promptSelect("Select matching Provisioning Profile:", [...profileOptions, "None / Automatic"]);
              if (chosenProfile !== "None / Automatic") {
                const idx = profileOptions.indexOf(chosenProfile);
                selectedProfileUuid = matchedProfiles[idx]!.uuid;
              }
            }
          } else {
            selectedTeam = await promptQuestion("Enter your 10-character Apple Developer Team ID: ");
          }

          if (selectedTeam && selectedTeam.length === 10) {
            const setupSpinner = ora("Configuring signing properties in project.pbxproj...").start();
            
            // Inject DEVELOPMENT_TEAM
            if (pbxprojContent.includes("DEVELOPMENT_TEAM")) {
              pbxprojContent = pbxprojContent.replace(/DEVELOPMENT_TEAM\s*=\s*[^;]+;/g, `DEVELOPMENT_TEAM = ${selectedTeam};`);
            } else {
              pbxprojContent = pbxprojContent.replace(/buildSettings\s*=\s*\{/g, `buildSettings = {\n\t\t\t\tDEVELOPMENT_TEAM = ${selectedTeam};`);
            }

            // Inject PROVISIONING_PROFILE_SPECIFIER and CODE_SIGN_STYLE if manual profile selected
            if (selectedProfileUuid) {
              if (pbxprojContent.includes("PROVISIONING_PROFILE_SPECIFIER")) {
                pbxprojContent = pbxprojContent.replace(/PROVISIONING_PROFILE_SPECIFIER\s*=\s*[^;]+;/g, `PROVISIONING_PROFILE_SPECIFIER = "${selectedProfileUuid}";`);
              } else {
                pbxprojContent = pbxprojContent.replace(/buildSettings\s*=\s*\{/g, `buildSettings = {\n\t\t\t\tPROVISIONING_PROFILE_SPECIFIER = "${selectedProfileUuid}";`);
              }

              if (pbxprojContent.includes("CODE_SIGN_STYLE")) {
                pbxprojContent = pbxprojContent.replace(/CODE_SIGN_STYLE\s*=\s*[^;]+;/g, `CODE_SIGN_STYLE = Manual;`);
              } else {
                pbxprojContent = pbxprojContent.replace(/buildSettings\s*=\s*\{/g, `buildSettings = {\n\t\t\t\tCODE_SIGN_STYLE = Manual;`);
              }
            }

            fs.writeFileSync(pbxprojPath, pbxprojContent, "utf-8");
            setupSpinner.succeed(`Configured DEVELOPMENT_TEAM to ${selectedTeam}` + (selectedProfileUuid ? ` and profile UUID to ${selectedProfileUuid}` : ""));
          } else {
              console.log(chalk.red("✖ Invalid or empty Development Team ID. iOS compilation might fail."));
          }
        }
      }

      let targetSimulator = options.device;
      if (!targetSimulator) {
        const config = loadConfig(projectRoot);
        const savedDefault = config.defaultIosSimulator;

        const xctraceResult = await runCommand("xcrun xctrace list devices");
        const lines = xctraceResult.stdout.split("\n").map(l => l.trim());
        
        let section: "none" | "devices" | "offline" | "simulators" = "none";
        const physicalDevices: { name: string; udid: string }[] = [];
        const simulators: { name: string; udid: string }[] = [];

        for (const line of lines) {
          if (line.startsWith("== Devices ==")) {
            section = "devices";
            continue;
          } else if (line.startsWith("== Devices Offline ==")) {
            section = "offline";
            continue;
          } else if (line.startsWith("== Simulators ==")) {
            section = "simulators";
            continue;
          } else if (line.startsWith("==") || line.length === 0) {
            continue;
          }

          const match = line.match(/(.+?)\s+\(([-A-Fa-f0-9]+)\)/);
          if (match && match[1] && match[2]) {
            const name = match[1].trim();
            const udid = match[2].trim();

            if (section === "devices") {
              const isMac = name.toLowerCase().includes("macbook") || 
                            name.toLowerCase().includes("mac mini") || 
                            name.toLowerCase().includes("macpro") || 
                            name.toLowerCase().includes("imac") ||
                            name.toLowerCase().includes("mac book");
              if (!isMac) {
                physicalDevices.push({ name, udid });
              }
            } else if (section === "simulators") {
              simulators.push({ name, udid });
            }
          }
        }

        const allAvailable = [...physicalDevices, ...simulators];
        const matchedDefault = allAvailable.find(d => d.udid === savedDefault || d.name === savedDefault);

        if (matchedDefault) {
          console.log(chalk.green(`Using default iOS device: ${matchedDefault.name} (${matchedDefault.udid})`));
          targetSimulator = matchedDefault.udid;
        } else if (physicalDevices.length > 0) {
          if (process.stdout.isTTY) {
            const optionsList = [
              ...physicalDevices.map(d => `📱 ${d.name} (Physical Device)`),
              ...simulators.map(d => `🖥️ ${d.name} (Simulator)`)
            ];
            const chosen = await promptSelect("Select target iOS device:", optionsList);
            const idx = optionsList.indexOf(chosen);
            const selectedDevice = allAvailable[idx]!;
            targetSimulator = selectedDevice.udid;
            
            const saveAsDefault = await promptQuestion("Would you like to set this device as your default target? (y/n): ");
            if (saveAsDefault.toLowerCase() === "y" || saveAsDefault.toLowerCase() === "yes") {
              saveConfig(projectRoot, { defaultIosSimulator: targetSimulator });
              console.log(chalk.green(`Saved ${selectedDevice.name} as default.`));
            }
          }
        } else if (simulators.length > 0) {
          if (process.stdout.isTTY) {
            const optionsList = simulators.map(d => `🖥️ ${d.name}`);
            const chosen = await promptSelect("Select target iOS simulator:", optionsList);
            const idx = optionsList.indexOf(chosen);
            const selectedDevice = simulators[idx]!;
            targetSimulator = selectedDevice.udid;
            
            const saveAsDefault = await promptQuestion("Would you like to set this simulator as default? (y/n): ");
            if (saveAsDefault.toLowerCase() === "y" || saveAsDefault.toLowerCase() === "yes") {
              saveConfig(projectRoot, { defaultIosSimulator: targetSimulator });
              console.log(chalk.green(`Saved ${selectedDevice.name} as default.`));
            }
          }
        }
      }

      let runCmd = "";
      if (isExpo) {
        console.log(chalk.blue(`\n🔨 Compiling and deploying iOS app via Expo CLI...`));
        const configArg = mode === "release" ? "--configuration Release" : "--configuration Debug";
        const schemeArg = `--scheme "${scheme}"`;
        const deviceArg = targetSimulator ? `--device "${targetSimulator}"` : "";
        runCmd = `npx expo run:ios ${configArg} ${schemeArg} ${deviceArg}`;
      } else {
        console.log(chalk.blue(`\n🔨 Compiling and deploying iOS app via React Native CLI...`));
        const configArg = mode === "release" ? "--mode Release" : "--mode Debug";
        const schemeArg = `--scheme "${scheme}"`;
        const simulatorArg = targetSimulator ? `--udid "${targetSimulator}"` : "";
        runCmd = `npx react-native run-ios ${configArg} ${schemeArg} ${simulatorArg}`;
      }

      console.log(chalk.cyan(`Running: ${runCmd}`));
      const runResult = await runCommandStreaming(runCmd, {
        prefix: "xcode",
        verbose: true,
      });

      if (runResult.code !== 0) {
        console.log(chalk.red(`✖ iOS run failed.`));
        process.exit(1);
      }

      console.log(chalk.green("\n✓ iOS run completed successfully!"));
    }
  });

function safeReadJson(filePath: string): Record<string, unknown> | null {
  try {
    if (!fs.existsSync(filePath)) return null;
    return JSON.parse(fs.readFileSync(filePath, "utf-8"));
  } catch {
    return null;
  }
}
